import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import apiClient from '@/lib/api-client';
import { Building2, Save, Settings } from 'lucide-react';

export function TenantSettingsPage() {
 const user = useAuthStore((s) => s.user);
 const queryClient = useQueryClient();
 const addToast = useToastStore((s) => s.addToast);

 const [form, setForm] = useState({
 name: '',
 address: '',
 phone: '',
 email: '',
 taxId: '',
 currency: 'NGN',
 timezone: 'Africa/Lagos',
 dateFormat: 'DD/MM/YYYY',
 lowStockThreshold: '10',
 taxRate: '7.50',
 taxLabel: 'VAT',
 receiptFooter: '',
 requireRequisition: false,
 allowGrnWithoutPO: false,
 autoConfirmInventory: true,
 poAutoApprove: '100000',
 poManagerApprove: '500000',
 });

 const { isLoading } = useQuery({
 queryKey: ['tenant', user?.tenantId],
 queryFn: async () => {
 const { data } = await apiClient.get(`/tenants/${user!.tenantId}`);
 const t = data.data;
 const s = t.settings || {};
 setForm({
 name: t.name || '',
 address: t.address || '',
 phone: t.phone || '',
 email: t.email || '',
 taxId: t.taxId || '',
 currency: s.currency || 'NGN',
 timezone: s.timezone || 'Africa/Lagos',
 dateFormat: s.dateFormat || 'DD/MM/YYYY',
 lowStockThreshold: String(s.lowStockThreshold || 10),
 taxRate: String(s.taxRate || 7.5),
 taxLabel: s.taxLabel || 'VAT',
 receiptFooter: s.receiptFooter || '',
 requireRequisition: s.procurement?.requireRequisition || false,
 allowGrnWithoutPO: s.procurement?.allowGrnWithoutPO || false,
 autoConfirmInventory: s.procurement?.autoConfirmInventory !== false,
 poAutoApprove: String(s.procurement?.approvalThresholds?.poAutoApprove || 100000),
 poManagerApprove: String(s.procurement?.approvalThresholds?.poManagerApprove || 500000),
 });
 return t;
 },
 enabled: !!user?.tenantId,
 });

 const saveMutation = useMutation({
 mutationFn: () => apiClient.patch(`/tenants/${user!.tenantId}`, {
 name: form.name,
 address: form.address,
 phone: form.phone,
 email: form.email,
 taxId: form.taxId,
 settings: {
 currency: form.currency,
 timezone: form.timezone,
 dateFormat: form.dateFormat,
 lowStockThreshold: parseInt(form.lowStockThreshold),
 taxRate: parseFloat(form.taxRate),
 taxLabel: form.taxLabel,
 receiptFooter: form.receiptFooter,
 procurement: {
 requireRequisition: form.requireRequisition,
 allowGrnWithoutPO: form.allowGrnWithoutPO,
 autoConfirmInventory: form.autoConfirmInventory,
 approvalThresholds: {
 poAutoApprove: parseInt(form.poAutoApprove),
 poManagerApprove: parseInt(form.poManagerApprove),
 },
 },
 },
 }),
 onSuccess: () => {
 addToast({ type: 'success', title: 'Settings saved' });
 queryClient.invalidateQueries({ queryKey: ['tenant'] });
 },
 onError: (err: any) => {
 addToast({ type: 'error', title: 'Failed to save', message: err?.response?.data?.error?.message });
 },
 });

 const update = (field: string, value: any) => setForm({ ...form, [field]: value });

 if (isLoading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

 return (
 <div className="space-y-6 max-w-3xl">
 <div>
 <h2 className="text-xl font-bold text-gray-900 ">Organization Settings</h2>
 <p className="text-sm text-gray-500 mt-1">Configure your business profile and procurement rules</p>
 </div>

 {/* Company Info */}
 <Card>
 <CardHeader><h3 className="font-semibold">Company Information</h3></CardHeader>
 <CardContent className="space-y-4">
 <Input label="Company Name" value={form.name} onChange={(e) => update('name', e.target.value)} />
 <Input label="Address" value={form.address} onChange={(e) => update('address', e.target.value)} />
 <div className="grid grid-cols-2 gap-4">
 <Input label="Phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
 <Input label="Email" value={form.email} onChange={(e) => update('email', e.target.value)} />
 </div>
 <Input label="Tax ID (TIN)" value={form.taxId} onChange={(e) => update('taxId', e.target.value)} />
 </CardContent>
 </Card>

 {/* Regional Settings */}
 <Card>
 <CardHeader><h3 className="font-semibold">Regional Settings</h3></CardHeader>
 <CardContent className="grid grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
 <select value={form.currency} onChange={(e) => update('currency', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white ">
 <option value="NGN">NGN (₦)</option>
 <option value="USD">USD ($)</option>
 <option value="GHS">GHS</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
 <select value={form.timezone} onChange={(e) => update('timezone', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white ">
 <option value="Africa/Lagos">Africa/Lagos</option>
 <option value="Africa/Nairobi">Africa/Nairobi</option>
 <option value="Africa/Accra">Africa/Accra</option>
 </select>
 </div>
 <Input label="Date Format" value={form.dateFormat} onChange={(e) => update('dateFormat', e.target.value)} />
 <Input label="Tax Rate (%)" type="number" step="0.01" value={form.taxRate} onChange={(e) => update('taxRate', e.target.value)} />
 <Input label="Tax Label" value={form.taxLabel} onChange={(e) => update('taxLabel', e.target.value)} />
 <Input label="Low Stock Threshold" type="number" value={form.lowStockThreshold} onChange={(e) => update('lowStockThreshold', e.target.value)} />
 <div className="col-span-2">
 <Input label="Receipt Footer Text" value={form.receiptFooter} onChange={(e) => update('receiptFooter', e.target.value)} placeholder="Thank you for your patronage!" />
 </div>
 </CardContent>
 </Card>

 {/* Procurement Settings */}
 <Card>
 <CardHeader><h3 className="font-semibold">Procurement Settings</h3></CardHeader>
 <CardContent className="space-y-4">
 <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
 <div>
 <p className="text-sm font-medium text-gray-900 ">Require Purchase Requisition</p>
 <p className="text-xs text-gray-500">Staff must submit requisition before PO can be created</p>
 </div>
 <label className="relative inline-flex items-center cursor-pointer">
 <input type="checkbox" checked={form.requireRequisition} onChange={(e) => update('requireRequisition', e.target.checked)} className="sr-only peer" />
 <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
 </label>
 </div>
 <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
 <div>
 <p className="text-sm font-medium text-gray-900 ">Allow GRN Without PO</p>
 <p className="text-xs text-gray-500">Receive goods even without a purchase order</p>
 </div>
 <label className="relative inline-flex items-center cursor-pointer">
 <input type="checkbox" checked={form.allowGrnWithoutPO} onChange={(e) => update('allowGrnWithoutPO', e.target.checked)} className="sr-only peer" />
 <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
 </label>
 </div>
 <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
 <div>
 <p className="text-sm font-medium text-gray-900 ">Auto-Confirm Inventory on GRN</p>
 <p className="text-xs text-gray-500">Automatically update stock when goods are received</p>
 </div>
 <label className="relative inline-flex items-center cursor-pointer">
 <input type="checkbox" checked={form.autoConfirmInventory} onChange={(e) => update('autoConfirmInventory', e.target.checked)} className="sr-only peer" />
 <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
 </label>
 </div>
 <div className="grid grid-cols-2 gap-4 pt-2">
 <Input label="Auto-Approve PO Below (₦)" type="number" value={form.poAutoApprove} onChange={(e) => update('poAutoApprove', e.target.value)} />
 <Input label="Manager Approve Up To (₦)" type="number" value={form.poManagerApprove} onChange={(e) => update('poManagerApprove', e.target.value)} />
 </div>
 <p className="text-xs text-gray-400">POs above the manager threshold require two approvals (Manager + Super Admin).</p>
 </CardContent>
 </Card>

 <div className="flex justify-end">
 <Button onClick={() => saveMutation.mutate()} isLoading={saveMutation.isPending}>
 <Save size={16} className="mr-1.5" /> Save All Settings
 </Button>
 </div>
 </div>
 );
}